import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Magnetic } from "@/components/motion/Magnetic";
import { calmEase } from "@/components/motion/Reveal";
import HeroCarousel from "@/components/HeroCarousel";

const heroContainer = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } };
const heroItem = {
    hidden: { opacity: 0, y: 36 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: calmEase } },
};
const FEATURED_INITIAL = { opacity: 0, y: 40 };
const FEATURED_VISIBLE = { opacity: 1, y: 0 };
const FEATURED_TRANSITION = { duration: 0.8, ease: calmEase, delay: 0.45 };

export default function HomeHero({ posts }) {
    const reduceMotion = useReducedMotion();

    return (
        <section className="relative border-b border-zinc-800 noise overflow-hidden" data-testid="hero">
            <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#FFD000]/10 blur-[120px]" aria-hidden="true" />
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-14 md:pb-20">
                <motion.div
                    className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end"
                    variants={heroContainer}
                    initial={reduceMotion ? false : "hidden"}
                    animate="show"
                >
                    <div className="lg:col-span-8 min-w-0">
                        <motion.div variants={heroItem} className="overline mb-6 flex items-center gap-3">
                            <span className="inline-block w-2 h-2 bg-[#FFD000] animate-pulse" />
                            // ISSUE Nº 001 — TIZONA TECH
                        </motion.div>
                        <motion.h1
                            variants={heroItem}
                            data-testid="hero-headline"
                            className="font-display text-white text-[17vw] sm:text-[14vw] lg:text-[10.5vw] xl:text-[9.5rem] leading-[0.82] tracking-tight"
                        >
                            TECH <span className="text-[#FFD000]">UNFILTERED.</span>
                        </motion.h1>
                    </div>
                    <div className="lg:col-span-4 min-w-0 lg:pb-3 lg:border-l lg:border-zinc-800 lg:pl-8">
                        <motion.p variants={heroItem} className="text-zinc-300 text-base md:text-lg leading-relaxed" data-testid="hero-tagline">
                            Reviews that don&apos;t pull punches. News that matters. Videos worth your time.
                            Welcome to the workshop.
                        </motion.p>
                        <motion.div variants={heroItem} className="mt-8 flex flex-wrap gap-3">
                            <Magnetic>
                                <Link to="/category/news" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-news">
                                    Latest News <ArrowRight size={16} weight="bold" />
                                </Link>
                            </Magnetic>
                            <Magnetic>
                                <Link to="/category/reviews" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-reviews">
                                    Read Reviews
                                </Link>
                            </Magnetic>
                        </motion.div>
                    </div>
                </motion.div>

                <div className="mt-12 md:mt-16">
                    {posts.length > 0 ? (
                        <motion.div
                            initial={reduceMotion ? false : FEATURED_INITIAL}
                            animate={FEATURED_VISIBLE}
                            transition={FEATURED_TRANSITION}
                        >
                            <HeroCarousel posts={posts.slice(0, 3)} />
                        </motion.div>
                    ) : (
                        <div className="grid lg:grid-cols-12 gap-6" aria-hidden="true">
                            <div className="lg:col-span-8 aspect-video border border-zinc-800 bg-zinc-900/40" />
                            <div className="hidden lg:block lg:col-span-4 border border-zinc-800 bg-zinc-900/40" />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
